/**
 * Даты календаря — в часах смотрящего.
 *
 * Сервер хранит моменты, а сетка дня — это стенные часы того, кто на неё смотрит: «10:00»
 * у московского менеджера и у новосибирского — разные моменты, и каждый видит свою сетку.
 * Поэтому границы дня и недели считаются здесь по локальному времени браузера, а на сервер
 * уходит момент в ISO.
 */

export type CalendarView = 'day' | 'week' | 'month'

export const MINUTES_IN_DAY = 24 * 60

/** Шаг сетки при перетаскивании и клике по слоту: четверть часа. */
export const SNAP_MINUTES = 15

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days,
    date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds())
}

/** Понедельник недели: в России неделя начинается с него. */
export function startOfWeek(date: Date): Date {
  const day = startOfDay(date)
  const shift = (day.getDay() + 6) % 7
  return addDays(day, -shift)
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

/** Видимый период: день; неделя с понедельника; месяц — шесть полных недель сетки. */
export function rangeOf(view: CalendarView, anchor: Date): { from: Date; to: Date; days: Date[] } {
  let from: Date
  let count: number
  if (view === 'day') {
    from = startOfDay(anchor)
    count = 1
  } else if (view === 'week') {
    from = startOfWeek(anchor)
    count = 7
  } else {
    from = startOfWeek(startOfMonth(anchor))
    count = 42
  }
  const days = Array.from({ length: count }, (_, index) => addDays(from, index))
  return { from, to: addDays(from, count), days }
}

/** Шаг навигации «назад/вперёд» в своём виде. */
export function shift(view: CalendarView, anchor: Date, direction: 1 | -1): Date {
  if (view === 'day') return addDays(anchor, direction)
  if (view === 'week') return addDays(anchor, 7 * direction)
  return new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1)
}

export function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

export function minutesOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes()
}

/** Момент дня по минутам от полуночи, с привязкой к сетке. */
export function atMinutes(day: Date, minutes: number): Date {
  const snapped = Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES
  const clamped = Math.max(0, Math.min(MINUTES_IN_DAY - SNAP_MINUTES, snapped))
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(clamped / 60), clamped % 60)
}

/** Значение для `<input type="datetime-local">` — местное время без пояса. */
export function toLocalInput(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Момент из `datetime-local`; пусто или мусор — null. */
export function fromLocalInput(value: string): Date | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const FORMATS = new Map<string, Intl.DateTimeFormat>()

/**
 * Форматтер из кеша: сетка недели — это 672 слота с подписью, и новый `Intl.DateTimeFormat`
 * на каждый стоил бы перерисовки в полсекунды при каждом движении мыши над сеткой.
 */
export function format(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`
  let cached = FORMATS.get(key)
  if (!cached) {
    cached = new Intl.DateTimeFormat(locale, options)
    FORMATS.set(key, cached)
  }
  return cached
}

export function time(date: Date, locale: string): string {
  return format(locale, { hour: '2-digit', minute: '2-digit' }).format(date)
}
